import { useState, useCallback, useEffect } from 'react';
import { Nav } from '../components/Nav';
import { useAuth } from '../hooks/useAuth';
import { fetchScheduleData, saveContacts } from '../services/api';
import { formatPhone } from '../utils/phone';
import type { Pianist, Conductor, NotificationContact, ContactsData } from '../types';

export function ContactsPage() {
  const { isAdminAuthenticated, loginForAdmin } = useAuth();

  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pianists, setPianists] = useState<Pianist[]>([]);
  const [conductors, setConductors] = useState<Conductor[]>([]);
  const [notificationContacts, setNotificationContacts] = useState<NotificationContact[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchScheduleData();
        setPianists(data.pianists);
        setConductors(data.conductors);
        setNotificationContacts(data.notificationContacts);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleLogin = useCallback(async () => {
    const success = await loginForAdmin(password);
    if (!success) {
      setLoginError(true);
    }
  }, [password, loginForAdmin]);

  // Pianist functions
  const addPianist = useCallback(() => {
    setPianists((prev) => [...prev, { id: Date.now().toString(), name: '', phone: '' }]);
  }, []);

  const updatePianist = useCallback((index: number, field: keyof Pianist, value: string) => {
    setPianists((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const deletePianist = useCallback((index: number) => {
    if (confirm('Are you sure you want to delete this pianist/organist?')) {
      setPianists((prev) => prev.filter((_, i) => i !== index));
    }
  }, []);

  // Conductor functions
  const addConductor = useCallback(() => {
    setConductors((prev) => [...prev, { id: Date.now().toString(), name: '', phone: '' }]);
  }, []);

  const updateConductor = useCallback((index: number, field: keyof Conductor, value: string) => {
    setConductors((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const deleteConductor = useCallback((index: number) => {
    if (confirm('Are you sure you want to delete this conductor?')) {
      setConductors((prev) => prev.filter((_, i) => i !== index));
    }
  }, []);

  // Contact functions
  const addContact = useCallback(() => {
    setNotificationContacts((prev) => [...prev, { id: Date.now().toString(), name: '', phone: '' }]);
  }, []);

  const updateContact = useCallback((index: number, field: keyof NotificationContact, value: string) => {
    setNotificationContacts((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const deleteContact = useCallback((index: number) => {
    if (confirm('Are you sure you want to delete this contact?')) {
      setNotificationContacts((prev) => prev.filter((_, i) => i !== index));
    }
  }, []);

  const handleSave = useCallback(async () => {
    try {
      const data: ContactsData = {
        pianists,
        conductors,
        notificationContacts,
      };
      const result = await saveContacts(data);
      if (result.success) {
        alert('Contacts saved successfully!');
      } else {
        alert('Failed to save contacts');
      }
    } catch (err) {
      alert(`Error saving contacts: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [pianists, conductors, notificationContacts]);

  const handlePhoneInput = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      updateFn: (index: number, field: 'phone', value: string) => void,
      index: number
    ) => {
      const formatted = formatPhone(e.target.value);
      e.target.value = formatted;
      updateFn(index, 'phone', formatted);
    },
    []
  );

  if (loading) {
    return (
      <>
        <Nav />
        <div className="page-container">
          <p>Loading...</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Nav />
        <div className="page-container">
          <p style={{ color: 'red' }}>Error: {error}</p>
        </div>
      </>
    );
  }

  if (!isAdminAuthenticated) {
    return (
      <>
        <Nav />
        <div className="login-container">
          <h2>Pianists & Contacts Login</h2>
          <input
            type="password"
            placeholder="Enter password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
          />
          <button className="btn-primary" onClick={handleLogin}>
            Login
          </button>
          {loginError && (
            <p style={{ color: '#dc3545' }}>Incorrect password</p>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <Nav />
      <div className="contacts-content contacts-content--show">
        <h1>Pianists & Contacts</h1>

        <h2>Pianists / Organists</h2>
        <p>
          These people will appear in the dropdown when selecting an accompanist
          for each song. They will also receive notifications about schedule
          updates and reminders.
        </p>

        <div className="actions-bar">
          <button className="btn-primary" onClick={addPianist}>
            + Add Pianist/Organist
          </button>
        </div>

        <div id="pianists-container">
          {pianists.map((pianist, index) => (
            <div key={pianist.id} className="contact-card">
              <input
                type="text"
                placeholder="Name"
                value={pianist.name}
                onChange={(e) => updatePianist(index, 'name', e.target.value)}
              />
              <input
                type="tel"
                placeholder="(123) 456-7890"
                value={pianist.phone}
                onInput={(e) =>
                  handlePhoneInput(
                    e as React.ChangeEvent<HTMLInputElement>,
                    updatePianist,
                    index
                  )
                }
              />
              <button
                className="btn-danger"
                onClick={() => deletePianist(index)}
              >
                Delete
              </button>
            </div>
          ))}
        </div>

        <h2>Conductors</h2>
        <p>
          These people will appear in the dropdown when selecting a music
          conductor for each week. They will also receive notifications about
          schedule updates and reminders.
        </p>

        <div className="actions-bar">
          <button className="btn-primary" onClick={addConductor}>
            + Add Conductor
          </button>
        </div>

        <div id="conductors-container">
          {conductors.map((conductor, index) => (
            <div key={conductor.id} className="contact-card">
              <input
                type="text"
                placeholder="Name"
                value={conductor.name}
                onChange={(e) => updateConductor(index, 'name', e.target.value)}
              />
              <input
                type="tel"
                placeholder="(123) 456-7890"
                value={conductor.phone}
                onInput={(e) =>
                  handlePhoneInput(
                    e as React.ChangeEvent<HTMLInputElement>,
                    updateConductor,
                    index
                  )
                }
              />
              <button
                className="btn-danger"
                onClick={() => deleteConductor(index)}
              >
                Delete
              </button>
            </div>
          ))}
        </div>

        <h2>Notification Contacts</h2>
        <p>
          These people will be notified about schedule updates and reminders.
        </p>

        <div className="actions-bar">
          <button className="btn-primary" onClick={addContact}>
            + Add Contact
          </button>
        </div>

        <div id="contacts-container">
          {notificationContacts.map((contact, index) => (
            <div key={contact.id} className="contact-card">
              <input
                type="text"
                placeholder="Name"
                value={contact.name}
                onChange={(e) => updateContact(index, 'name', e.target.value)}
              />
              <input
                type="tel"
                placeholder="(123) 456-7890"
                value={contact.phone}
                onInput={(e) =>
                  handlePhoneInput(
                    e as React.ChangeEvent<HTMLInputElement>,
                    updateContact,
                    index
                  )
                }
              />
              <button
                className="btn-danger"
                onClick={() => deleteContact(index)}
              >
                Delete
              </button>
            </div>
          ))}
        </div>

        <div className="actions-bar" style={{ marginTop: '30px' }}>
          <button className="btn-success" onClick={handleSave}>
            Save All Changes
          </button>
        </div>
      </div>
    </>
  );
}
